import * as React from "react";
import { observer } from "mobx-react-lite";
import { classNames } from "src/common/classNames";
import styles from "./Bom2aPositionsTable.module.less";
import { Bom2aColumn } from "./Bom2aColumn";
import { Bom2aRowSelection } from "./components/Bom2aSelection";
import { Bom2aPositionsStore } from "../Bom2aPositionsStore";
import { Bom2aPositionItem } from "./Bom2aPositionItem";

type PropsBom2aPositionsTable = {
  store: Bom2aPositionsStore;
};

export const Bom2aPositionsTable: React.FC<PropsBom2aPositionsTable> = observer(
  (props) => {
    const { store } = props;
    const { positions, finalColumns: columns } = store;
    const gridTemplateColumns = makeGridTemplates(columns);

    React.useEffect(() => {
      store.sheetStore.init();
    }, []);

    React.useEffect(() => () => {
      store.sheetStore.postUpdate();
    });

    const refHeader = React.useRef<HTMLDivElement>(null);
    const refBody = React.useRef<HTMLDivElement>(null);
    React.useEffect(() => {
      const body = refBody.current;
      const resizeObserver = new ResizeObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.target === body) {
            if (refHeader.current) {
              refHeader.current.style.width = `${entry.contentRect.width}px`;
            }
          }
        });
      });
      if (body) {
        resizeObserver.observe(body);
      }
      return () => {
        resizeObserver.disconnect();
      };
    }, [refBody.current]);

    return (
      <div className={styles.tableFrame}>
        {/* Header */}
        <div
          className={styles.header}
          ref={refHeader}
          style={{ gridTemplateColumns }}
        >
          {columns.map((col) => (
            <div key={`hd:${col.key}`} className={classNames([styles.th])}>
              {col.title}
            </div>
          ))}
        </div>

        {/* positions */}
        <div
          ref={refBody}
          className={styles.body}
          onScroll={(e) => {
            if (refHeader.current) {
              refHeader.current.scrollLeft = e.currentTarget.scrollLeft;
            }
          }}
        >
          <div className={styles.cells} style={{ gridTemplateColumns }}>
            {positions.map((pos) => (
              <React.Fragment key={pos.materialTypeId}>
                <div className={classNames([styles.posHd])}>
                  <Bom2aRowSelection material={pos} store={store} />
                </div>
                <Bom2aPositionItem position={pos} store={store} />
                {/* position rows */}
                {!store.collapsed.has(pos.materialTypeId) &&
                  pos.bomPositionRows.map((row, rowIndex) => (
                    <React.Fragment key={row.id}>
                      {columns.map((col) => (
                        <div key={col.key} className={classNames([styles.td])}>
                          {col.render(row, rowIndex)}
                        </div>
                      ))}
                    </React.Fragment>
                  ))}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
    );
  },
);

export const makeGridTemplates = (columns: Bom2aColumn[]): string =>
  columns
    .map(({ width, minWidth }) => {
      if (!width && !minWidth) return "minmax(0,1fr)";
      let strWidth: string;
      if (typeof width === "number") {
        strWidth = `${width}px`;
        if (!minWidth) return strWidth;
      } else {
        strWidth = width || "1fr";
      }
      if (!minWidth && !/fr$/.test(strWidth)) {
        return strWidth;
      }
      const minVal = minWidth ? `${minWidth}px` : "0";
      return `minmax(${minVal},${strWidth})`;
    })
    .join(" ");
